import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  createTheme,
  ThemeProvider,
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Paper,
  Stack,
  InputAdornment,
  IconButton,
  Dialog,
  DialogContent,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { Link as RouterLink } from "react-router-dom";
import {
  Visibility,
  VisibilityOff,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from "@mui/icons-material";
import Loader from "../StyleComponant/Loader";

const loginPageTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#D4AF37',
    },
  },
});

export default function LoginPage() {
  const [usernameOrEmail, setusernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPanel, setShowForgotPanel] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [showLogin, setShowLogin] = useState(true); // Default to true for immediate rendering

  const [loginSuccessOpen, setLoginSuccessOpen] = useState(false);

  const { login, forgotPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!usernameOrEmail || !password) {
      setError("Please enter both username/email and password.");
      setLoading(false);
      return;
    }
    
    try {
      const result = await login(usernameOrEmail, password);

      if (result.success) {
        setLoginSuccessOpen(true);
        setTimeout(() => {
          setLoginSuccessOpen(false);
          switch (result.user.role) {
            case "talent":
              navigate("/db-shiksha/gatep/talent");
              break;
            case "employer":
              navigate("/db-shiksha/gatep/employer");
              break;
            case "admin":
              navigate("/db-shiksha/gatep/admin");
              break;
            default:
              navigate("/db-shiksha");
          }
        }, 2000);
      } else {
        setError(result.error || "Login failed. Please check your credentials.");
        setLoading(false);
      }
    } catch (err) {
      setError("A network error occurred. Please try again.");
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResetSent(false);
    setError("");

    if (!resetEmail) {
      setError("Please enter your email.");
      setLoading(false);
      return;
    }

    try {
      const result = await forgotPassword(resetEmail);
      if (result.success) {
        setResetSent(true);
      } else {
        setError(result.error || "Failed to send reset email.");
      }
    } catch (err) {
      setError("Failed to send reset link. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const gradientButtonStyle = {
    height: "52.5px",
    py: 1.5,
    fontSize: "1rem",
    fontWeight: 600,
    color: "white",
    background: "linear-gradient(45deg, #0a0a0aff 30%, #0c0b0cff 60%)",
    border: "1px solid rgba(212, 175, 55, 0.3)",
    borderRadius: "50px",
    boxShadow: "0 3px 5px 2px rgba(0, 0, 0, 0.3)",
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1a1a1aff 30%, #2a2a2aff 90%)",
      transform: "scale(1.02)",
      boxShadow: "0 5px 15px 2px rgba(212, 175, 55, 0.2)",
    },
    "&.Mui-disabled": {
      background: "linear-gradient(45deg, #2c2c2c 30%, #3a3a3a 90%)",
      color: "rgba(255, 255, 255, 0.5)",
    },
  };

  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(0, 0, 0, 0.2)",
      color: "#fff",
      borderRadius: 3,
      "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
      "&.Mui-focused fieldset": { borderColor: "#D4AF37" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#D4AF37" },
  };

  return (
    <ThemeProvider theme={loginPageTheme}>
      <Dialog
        open={loginSuccessOpen}
        PaperProps={{
          sx: { borderRadius: "12px", minWidth: "320px", bgcolor: "#1a1a1a", color: "#fff" },
        }}
      >
        <DialogContent sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 60, color: "success.main" }} />
          <Typography variant="h5" fontWeight="bold">Login Successful!</Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.7)">Redirecting you now...</Typography>
        </DialogContent>
      </Dialog>

      <Box
        sx={{
          position: "relative",
          minHeight: "100vh",
          width: "100vw",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#050505", // Fallback color
        }}
      >
        <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
            opacity: 0.6,
          }}
        >
          <source src="/assets/ai.mp4" type="video/mp4" />
          <source src="/assets/Blue.mp4" type="video/mp4" />
        </video>

        {showLogin && (
          <Paper
            elevation={24}
            sx={{
              position: "relative",
              zIndex: 1,
              p: { xs: 4, md: 6 },
              width: 450,
              maxWidth: "90vw",
              borderRadius: 6,
              color: "#fff",
              background: "rgba(15, 15, 15, 0.75)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(212, 175, 55, 0.15)",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
            }}
          >
            <Stack spacing={4}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={800} gutterBottom sx={{ letterSpacing: '-0.5px' }}>
                  {showForgotPanel ? "Reset Access" : "GateP Portal"}
                </Typography>
                <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.5)" }}>
                  {showForgotPanel ? "Enter email for recovery link" : "Secure Authentication Required"}
                </Typography>
              </Box>

              {showForgotPanel ? (
                <form onSubmit={handleForgotPassword}>
                  <Stack spacing={3}>
                    <TextField
                      variant="outlined"
                      label="Email Address"
                      type="email"
                      fullWidth
                      required
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      sx={textFieldStyle}
                    />
                    <Button type="submit" fullWidth disabled={loading} sx={gradientButtonStyle}>
                      {loading ? <Loader size="1.5rem" color="#fff" /> : "Send Reset Link"}
                    </Button>
                    {resetSent && (
                      <Typography align="center" sx={{ color: "success.light", bgcolor: "rgba(76, 175, 80, 0.1)", p: 2, borderRadius: 2 }}>
                        Instructions sent if email is registered.
                      </Typography>
                    )}
                    <Link
                      component="button"
                      onClick={() => { setShowForgotPanel(false); setError(""); setResetSent(false); }}
                      sx={{ color: "rgba(255,255,255,0.4)", alignSelf: "center", mt: 1 }}
                    >
                      Return to Login
                    </Link>
                  </Stack>
                </form>
              ) : (
                <form onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <TextField
                      variant="outlined"
                      label="Username or Email"
                      fullWidth
                      required
                      value={usernameOrEmail}
                      onChange={(e) => setusernameOrEmail(e.target.value)}
                      sx={textFieldStyle}
                    />
                    <TextField
                      variant="outlined"
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      sx={textFieldStyle}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ color: "rgba(255,255,255,0.5)" }}>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button type="submit" fullWidth disabled={loading} sx={gradientButtonStyle}>
                      {loading ? <Loader size="1.5rem" color="#fff" /> : "Authorize Access"}
                    </Button>
                    {error && (
                      <Typography color="error" align="center" sx={{ bgcolor: "rgba(211, 47, 47, 0.1)", p: 1.5, borderRadius: 2 }}>
                        {error}
                      </Typography>
                    )}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Link component={RouterLink} to="/db-shiksha/gatep/register" sx={{ color: "#D4AF37", fontWeight: 600 }}>
                        Create Account
                      </Link>
                      <Link component="button" onClick={() => { setShowForgotPanel(true); setError(""); }} sx={{ color: "rgba(255,255,255,0.4)" }}>
                        Forgot Credentials?
                      </Link>
                    </Stack>
                  </Stack>
                </form>
              )}
            </Stack>
          </Paper>
        )}
      </Box>
    </ThemeProvider>
  );
}